import { useState, useEffect } from 'react';
import { clsx } from 'clsx';
import type { StaleDataBannerProps } from './MasterDocument.types';
import { useMasterDocumentTranslation } from './i18n';

/**
 * StaleDataBanner — avisa que los estados de cumplimiento pueden estar desactualizados.
 *
 * Verifica cada 5 segundos si han pasado más de `staleThresholdSeconds` desde `fetchedAt`.
 * Renderiza null mientras los datos son recientes para evitar ruido visual.
 */
export function StaleDataBanner({
  fetchedAt,
  staleThresholdSeconds = 30,
  onRefresh,
  isRefreshing = false,
  className,
}: StaleDataBannerProps) {
  const { t } = useMasterDocumentTranslation();
  const [isStale, setIsStale] = useState(() => {
    return (Date.now() - new Date(fetchedAt).getTime()) / 1000 > staleThresholdSeconds;
  });

  useEffect(() => {
    const check = () => {
      const ageSeconds = (Date.now() - new Date(fetchedAt).getTime()) / 1000;
      setIsStale(ageSeconds > staleThresholdSeconds);
    };

    check();
    const id = setInterval(check, 5000);
    return () => clearInterval(id);
  }, [fetchedAt, staleThresholdSeconds]);

  if (!isStale) return null;

  return (
    <div
      role="alert"
      className={clsx(
        'flex items-center justify-between gap-3 rounded-lg px-4 py-2.5',
        'bg-amber-50 border border-amber-200',
        'dark:bg-amber-950/20 dark:border-amber-800',
        className,
      )}
    >
      <div className="flex items-center gap-2 min-w-0">
        {/* Clock icon — inline para evitar dependencia adicional */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
          className="w-4 h-4 flex-shrink-0 text-amber-600 dark:text-amber-400"
        >
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm.75-13a.75.75 0 0 0-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 0 0 0-1.5h-3.25V5Z"
            clipRule="evenodd"
          />
        </svg>
        <span className="text-sm text-amber-800 dark:text-amber-200 truncate">
          {t('master_document.staleData.note')}
        </span>
      </div>

      <button
        type="button"
        onClick={onRefresh}
        disabled={isRefreshing}
        className={clsx(
          'flex items-center gap-1 text-xs font-semibold flex-shrink-0 transition-colors',
          'text-amber-700 dark:text-amber-300',
          'hover:text-amber-900 dark:hover:text-amber-100',
          isRefreshing && 'opacity-60 cursor-not-allowed',
        )}
        aria-label={t('master_document.staleData.refresh')}
      >
        {isRefreshing ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
            className="w-3.5 h-3.5 animate-spin"
          >
            <path
              fillRule="evenodd"
              d="M15.312 11.424a5.5 5.5 0 0 1-9.201 2.466l-.312-.311h2.433a.75.75 0 0 0 0-1.5H3.989a.75.75 0 0 0-.75.75v4.242a.75.75 0 0 0 1.5 0v-2.43l.31.31a7 7 0 0 0 11.712-3.138.75.75 0 0 0-1.449-.39Zm1.23-3.723a.75.75 0 0 0 .219-.53V2.929a.75.75 0 0 0-1.5 0V5.36l-.31-.31A7 7 0 0 0 3.239 8.188a.75.75 0 1 0 1.448.389A5.5 5.5 0 0 1 13.89 6.11l.311.31h-2.432a.75.75 0 0 0 0 1.5h4.243a.75.75 0 0 0 .53-.219Z"
              clipRule="evenodd"
            />
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
            className="w-3.5 h-3.5"
          >
            <path
              fillRule="evenodd"
              d="M15.312 11.424a5.5 5.5 0 0 1-9.201 2.466l-.312-.311h2.433a.75.75 0 0 0 0-1.5H3.989a.75.75 0 0 0-.75.75v4.242a.75.75 0 0 0 1.5 0v-2.43l.31.31a7 7 0 0 0 11.712-3.138.75.75 0 0 0-1.449-.39Zm1.23-3.723a.75.75 0 0 0 .219-.53V2.929a.75.75 0 0 0-1.5 0V5.36l-.31-.31A7 7 0 0 0 3.239 8.188a.75.75 0 1 0 1.448.389A5.5 5.5 0 0 1 13.89 6.11l.311.31h-2.432a.75.75 0 0 0 0 1.5h4.243a.75.75 0 0 0 .53-.219Z"
              clipRule="evenodd"
            />
          </svg>
        )}
        {t('master_document.chrome.refresh')}
      </button>
    </div>
  );
}
